var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import localforage from "localforage";
import { Color, Euler, Quaternion } from "three";
import { AskOkMessage, convertToNumber4, degToRad } from "flavr-common";
import { Vector3 } from "flavr-math";
import { RosBagPointCloudIdentifierMessage } from "@messages/rosbag/RosBagMessages";
import { busService } from "@modules/bus";
import { rosBagUIService } from "@modules/rosbag";
import { userMeshSharingService } from "./UserMeshSharingService";
import { useModelUploadStateStore } from "../store/model-upload-state.store";
export var ModelUploadService;
(function (ModelUploadService) {
    let FileType;
    (function (FileType) {
        FileType[FileType["rosbag"] = 0] = "rosbag";
        FileType[FileType["stl"] = 1] = "stl";
        FileType[FileType["glb"] = 2] = "glb";
        FileType[FileType["gltf"] = 3] = "gltf";
        FileType[FileType["fbx"] = 4] = "fbx";
        FileType[FileType["threemf"] = 5] = "threemf";
        FileType[FileType["obj"] = 6] = "obj";
        FileType[FileType["ply"] = 7] = "ply";
        FileType[FileType["invalid"] = 8] = "invalid";
    })(FileType = ModelUploadService.FileType || (ModelUploadService.FileType = {}));
    function getFileType(file) {
        // mesh formats
        if (file.name.toLowerCase().endsWith(".gltf")) {
            return FileType.gltf;
        }
        if (file.name.toLowerCase().endsWith(".glb")) {
            return FileType.glb;
        }
        if (file.name.toLowerCase().endsWith(".fbx")) {
            return FileType.fbx;
        }
        if (file.name.toLowerCase().endsWith(".3mf")) {
            return FileType.threemf;
        }
        if (file.name.toLowerCase().endsWith(".obj")) {
            return FileType.obj;
        }
        if (file.name.toLowerCase().endsWith(".stl")) {
            return FileType.stl;
        }
        if (file.name.toLowerCase().endsWith(".ply")) {
            return FileType.ply;
        }
        // rosbag format
        if (file.name.toLowerCase().endsWith(".bag")) {
            return FileType.rosbag;
        }
        return FileType.invalid;
    }
    ModelUploadService.getFileType = getFileType;
    function isFileValid(file) {
        return getFileType(file) !== FileType.invalid;
    }
    ModelUploadService.isFileValid = isFileValid;
    function prepareRosbagFile(file) {
        return new Promise((resolve, reject) => {
            localforage
                .getItem(`ROSBAG/${file.name}`)
                .then((blob) => {
                if (!blob) {
                    blob = new Blob([file], { type: "rosbag/bag" });
                    localforage.setItem(`ROSBAG/${file.name}`, blob).then(() => {
                        resolve();
                    });
                }
            })
                .catch((error) => {
                reject(error);
            });
        });
    }
    ModelUploadService.prepareRosbagFile = prepareRosbagFile;
    function clearPendingRosbagFiles() {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            promises.push(localforage.iterate((_value, key) => {
                if (key.startsWith("ROSBAG/")) {
                    promises.push(localforage.removeItem(key));
                }
            }));
            yield Promise.all(promises);
        });
    }
    ModelUploadService.clearPendingRosbagFiles = clearPendingRosbagFiles;
    function uploadFile(file, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const type = getFileType(file);
            let result = { success: false, error: "Upload result unknown" };
            try {
                switch (type) {
                    case FileType.rosbag:
                        useModelUploadStateStore.getState().setInProgress(ModelUploadService.FileType.rosbag);
                        yield uploadRosbagFile(file, options);
                        break;
                    case FileType.stl:
                    case FileType.gltf:
                    case FileType.glb:
                    case FileType.fbx:
                    case FileType.threemf:
                    case FileType.obj:
                    case FileType.ply:
                        useModelUploadStateStore.getState().setInProgress(type);
                        yield uploadMeshFile(file, options);
                        break;
                    default:
                        result = { success: false, error: "Invalid file type" };
                }
                result = { success: true };
            }
            catch (error) {
                let message = "Unknown Error";
                if (error instanceof Error) {
                    message = error.message;
                }
                else if (typeof error === "string") {
                    message = error;
                }
                result = { success: false, error: message };
            }
            finally {
                useModelUploadStateStore.getState().setFinished();
            }
            return result;
        });
    }
    ModelUploadService.uploadFile = uploadFile;
    function uploadMeshFile(file, options) {
        return __awaiter(this, void 0, void 0, function* () {
            yield userMeshSharingService.processMeshFromFile(file, options.position, options.rotation, options.factorToScaleToMeter, options.scalable, options.outline, options.color, options.useMeshCollider);
        });
    }
    function uploadRosbagFile(file, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const position = new Vector3(options.position[0], options.position[1], options.position[2]);
            const rotation = new Vector3(degToRad(options.rotation[0]), degToRad(options.rotation[1]), degToRad(options.rotation[2]));
            const rotationQ = new Quaternion();
            rotationQ.setFromEuler(new Euler(rotation.x, rotation.y, rotation.z));
            const color = new Color(options.color);
            const isIdentifierUsed = !(yield busService
                .connection("ModelUploadService")
                .askByMessageType(new RosBagPointCloudIdentifierMessage(options.identifier), AskOkMessage));
            if (isIdentifierUsed) {
                throw new Error(`Identifier "${options.identifier}" already in use`);
            }
            rosBagUIService.createModel(file.name, options.topic, options.identifier, position, convertToNumber4(rotationQ), color, options.pointSize, options.syncWithOthers, options.useTransform);
        });
    }
})(ModelUploadService || (ModelUploadService = {}));
